import { useState } from 'react';
import { useTodayEvent } from '../hooks/useEventData';
import { useAuth } from '../context/AuthContext';
import { useGroupData } from '../hooks/useGroupData';
import { formatDate, formatTime, getMapsUrl, formatPhone, getEventTypeBorderColor } from '../utils/helpers';
import { updateAttendance, claimDriving, releaseDriving } from '../api/client';
import EventBadge from '../components/EventBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import type { AttendanceStatus, EventType } from '../types';

const typeBg: Record<EventType, string> = {
  practice: 'border-l-practice',
  regatta: 'border-l-regatta',
  competition: 'border-l-competition',
};

export default function TodayViewB() {
  const { user, kids: myKids } = useAuth();
  const { event, nextEvent, attendance, driving, loading, error, refresh } = useTodayEvent();
  const { members, kids: allKids } = useGroupData();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleAttendance = async (kidId: string, current: AttendanceStatus) => {
    if (!event) return;
    const next: AttendanceStatus = current === 'going' ? 'not_going' : 'going';
    setActionLoading(`att-${kidId}`);
    try {
      await updateAttendance(event.id, kidId, next);
      await refresh();
    } finally { setActionLoading(null); }
  };

  const handleDrive = async (type: 'pickup' | 'return') => {
    if (!event) return;
    setActionLoading(`drive-${type}`);
    try {
      await claimDriving(event.id, type);
      await refresh();
    } finally { setActionLoading(null); }
  };

  const handleReleaseDrive = async (type: 'pickup' | 'return') => {
    if (!event) return;
    setActionLoading(`release-${type}`);
    try {
      await releaseDriving(event.id, type);
      await refresh();
    } finally { setActionLoading(null); }
  };

  const getDriverName = (id: string | null) => id ? members.find(m => m.id === id)?.name || 'Unknown' : null;
  const getDriverPhone = (id: string | null) => id ? members.find(m => m.id === id)?.phoneNumber || null : null;
  const getKidAttendance = (kidId: string): AttendanceStatus => attendance.find(a => a.kidId === kidId)?.status || 'no_response';

  if (loading) return <LoadingSpinner className="mt-20" />;
  if (error) return <div className="p-6 text-center text-cancelled">{error}</div>;

  if (!event) {
    return (
      <div className="px-4 pt-5 max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white font-mono tracking-tight">{formatDate(new Date().toISOString().split('T')[0])}</h1>
        </div>
        <div className="border-l-4 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 pl-4 py-6 pr-4">
          <p className="font-semibold text-gray-600 dark:text-gray-300">No event scheduled</p>
          <p className="text-sm text-gray-400 mt-1">Rest day — check back tomorrow</p>
        </div>
        {nextEvent && (
          <div className={`mt-4 border-l-4 ${typeBg[nextEvent.type]} bg-white dark:bg-gray-900 pl-4 py-3 pr-4`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">Next</span>
              <EventBadge type={nextEvent.type} size="sm" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(nextEvent.date)}</p>
            <p className="text-xs text-gray-500 font-mono">{formatTime(nextEvent.startTime)}-{formatTime(nextEvent.endTime)} &bull; {nextEvent.locationName}</p>
          </div>
        )}
      </div>
    );
  }

  const pickupDriver = getDriverName(driving?.driverId || null);
  const pickupDriverPhone = getDriverPhone(driving?.driverId || null);
  const returnDriverId = driving?.returnDriverId || driving?.driverId || null;
  const returnDriver = getDriverName(returnDriverId);
  const isMyPickup = driving?.driverId === user?.id;
  const isMyReturn = driving?.returnDriverId === user?.id;
  const goingCount = allKids.filter(k => getKidAttendance(k.id) === 'going').length;
  const noResponseCount = allKids.filter(k => getKidAttendance(k.id) === 'no_response').length;

  return (
    <div className="px-4 pt-5 pb-6 max-w-lg mx-auto">
      {/* Compact header */}
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white font-mono tracking-tight">{formatDate(event.date)}</h1>
        <EventBadge type={event.type} status={event.status} />
      </div>

      {event.status === 'cancelled' ? (
        <div className="border-l-4 border-cancelled bg-cancelled/5 dark:bg-cancelled/10 pl-4 py-4 pr-4 mt-3">
          <p className="font-bold text-cancelled text-sm uppercase tracking-wide">Cancelled</p>
          {event.notes && <p className="text-sm text-gray-500 mt-1">{event.notes}</p>}
        </div>
      ) : (
        <>
          {/* Dense info strip */}
          <div className={`border-l-4 ${typeBg[event.type]} bg-white dark:bg-gray-900 mt-3`}>
            {event.title && (
              <div className="px-4 pt-3 pb-0">
                <p className="font-bold text-gray-900 dark:text-white text-sm">{event.title}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-px bg-gray-100 dark:bg-gray-800">
              {/* Time cell */}
              <div className="bg-white dark:bg-gray-900 px-4 py-3">
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">Time</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                  {formatTime(event.startTime)}-{formatTime(event.endTime)}
                </p>
              </div>
              {/* Pickup time cell */}
              <div className="bg-white dark:bg-gray-900 px-4 py-3">
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">Pickup</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                  {event.pickupTime ? formatTime(event.pickupTime) : '--:--'}
                </p>
              </div>
              {/* Location cell */}
              <div className="bg-white dark:bg-gray-900 px-4 py-3 col-span-2">
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">Location</p>
                <a
                  href={getMapsUrl(event.locationName)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-practice dark:text-practice-light hover:underline"
                >
                  {event.locationName} &nearr;
                </a>
              </div>
            </div>
            {driving?.pickupSpot && (
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-500"><span className="font-semibold">Pickup spot:</span> {driving.pickupSpot}</p>
              </div>
            )}
          </div>

          {/* Driver rows */}
          <div className="mt-3 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
            {/* Pickup driver */}
            <div className="px-4 py-3 flex items-center justify-between min-h-tap">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${pickupDriver ? 'bg-green-500' : 'bg-warning animate-pulse'}`} />
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">Driver &rarr;</p>
                  {pickupDriver ? (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {pickupDriver} {pickupDriverPhone && <span className="text-gray-400 font-mono text-xs">{formatPhone(pickupDriverPhone)}</span>}
                    </p>
                  ) : (
                    <p className="text-sm font-medium text-warning">Needs driver</p>
                  )}
                </div>
              </div>
              {pickupDriver ? (
                isMyPickup && (
                  <button onClick={() => handleReleaseDrive('pickup')} disabled={actionLoading === 'release-pickup'}
                    className="text-xs font-medium text-gray-400 hover:text-cancelled min-h-tap flex items-center px-2">Release</button>
                )
              ) : (
                <button onClick={() => handleDrive('pickup')} disabled={actionLoading === 'drive-pickup'}
                  className="text-xs font-bold text-white bg-practice hover:bg-practice-dark px-3 py-1.5 rounded min-h-tap flex items-center transition-colors disabled:opacity-50">
                  {actionLoading === 'drive-pickup' ? '...' : "I'll Drive"}
                </button>
              )}
            </div>
            {/* Return driver */}
            <div className="px-4 py-3 flex items-center justify-between min-h-tap">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${driving?.returnDriverId || pickupDriver ? 'bg-green-500' : 'bg-warning animate-pulse'}`} />
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">&larr; Return</p>
                  {driving?.returnDriverId ? (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{returnDriver}</p>
                  ) : pickupDriver ? (
                    <p className="text-sm text-gray-400">= {pickupDriver} <span className="text-[10px]">(default)</span></p>
                  ) : (
                    <p className="text-sm font-medium text-warning">Needs driver</p>
                  )}
                </div>
              </div>
              {driving?.returnDriverId ? (
                isMyReturn && (
                  <button onClick={() => handleReleaseDrive('return')} disabled={actionLoading === 'release-return'}
                    className="text-xs font-medium text-gray-400 hover:text-cancelled min-h-tap flex items-center px-2">Release</button>
                )
              ) : (
                <button onClick={() => handleDrive('return')} disabled={actionLoading === 'drive-return'}
                  className="text-xs font-bold text-practice dark:text-practice-light border border-practice/30 hover:bg-practice/10 px-3 py-1.5 rounded min-h-tap flex items-center transition-colors disabled:opacity-50">
                  {actionLoading === 'drive-return' ? '...' : "I'll Drive Back"}
                </button>
              )}
            </div>
          </div>

          {/* Compact attendance grid */}
          <div className="mt-3">
            <div className="flex items-center justify-between px-1 mb-2">
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">Crew ({goingCount}/{allKids.length})</p>
              {noResponseCount > 0 && (
                <p className="text-[10px] font-bold text-warning">{noResponseCount} pending</p>
              )}
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 divide-y divide-gray-50 dark:divide-gray-800/50">
              {allKids.map(kid => {
                const status = getKidAttendance(kid.id);
                const isMine = myKids.some(k => k.id === kid.id) || user?.role === 'admin';
                return (
                  <button
                    key={kid.id}
                    onClick={() => isMine && handleAttendance(kid.id, status)}
                    disabled={!isMine || actionLoading === `att-${kid.id}`}
                    className={`w-full flex items-center justify-between px-4 py-2.5 min-h-tap transition-colors ${
                      isMine ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 active:bg-gray-100 dark:active:bg-gray-800' : 'cursor-default'
                    }`}
                  >
                    <span className={`text-sm ${status === 'not_going' ? 'text-gray-300 dark:text-gray-600 line-through' : 'text-gray-900 dark:text-white'}`}>
                      {kid.name}
                      <span className="text-gray-400 dark:text-gray-500 text-xs ml-1.5">
                        {members.find(m => m.id === kid.parentId)?.name}
                      </span>
                    </span>
                    <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                      status === 'going' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' :
                      status === 'not_going' ? 'bg-gray-100 dark:bg-gray-800 text-gray-400' :
                      'bg-warning/10 text-warning'
                    }`}>
                      {status === 'going' ? '\u2713' : status === 'not_going' ? '\u2715' : '?'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          {event.notes && (
            <div className="mt-3 border-l-4 border-warning/40 bg-warning/5 dark:bg-warning/10 pl-4 py-3 pr-4">
              <p className="text-[10px] font-bold tracking-widest uppercase text-warning-dark dark:text-warning mb-0.5">Note</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{event.notes}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
